import { useEffect, useCallback, useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'

import { ArrayUtils } from '@core/arrayUtils'
import { SortOrder } from '@core/sortOrder'

import { useSurveyId } from '@webapp/store/survey'
import { useAsyncGetRequest, useOnUpdate } from '@webapp/components/hooks'
import { getLimit, getOffset, getSearch, getSort, updateQuery } from '@webapp/components/Table/tableLink'
import { TablesActions, useTableMaxRows, useTableSort, useTableVisibleColumns } from '@webapp/store/ui/tables'

export const useTable = ({
  columns,
  keyExtractor,
  moduleApiUri,
  module,
  restParams,
  onRowClick: onRowClickProp,
  selectable,
  selectOnClick,
}) => {
  const dispatch = useDispatch()

  const [totalCount, setTotalCount] = useState(0)

  const visibleColumnKeysInStore = useTableVisibleColumns(module)
  const visibleColumnKeys = useMemo(
    () => visibleColumnKeysInStore ?? columns?.filter((column) => !column.hidden).map((column) => column.key) ?? [],
    [visibleColumnKeysInStore, columns]
  )
  const visibleColumns = useMemo(
    () => columns?.filter((column) => visibleColumnKeys.includes(column.key)) ?? [],
    [visibleColumnKeys, columns]
  )
  const limitInState = useTableMaxRows(module)
  const limitInLink = getLimit()
  const limit = limitInState ?? limitInLink

  const navigate = useNavigate()
  const surveyId = useSurveyId()
  const apiUri = moduleApiUri || `/api/survey/${surveyId}/${module}`

  const offset = getOffset()
  const sortInState = useTableSort(module)
  const sortInLink = getSort()
  const sort = sortInState ?? sortInLink
  const search = getSearch()

  const {
    data: { list } = { list: [] },
    dispatch: fetchData,
    loading: loadingData,
  } = useAsyncGetRequest(apiUri, {
    params: {
      ...(offset ? { offset } : {}),
      limit,
      sortBy: sort.by,
      sortOrder: sort.order,
      search,
      ...restParams,
    },
  })
  const {
    data: { count } = { count: 0 },
    dispatch: fetchCount,
    loading: loadingCount,
  } = useAsyncGetRequest(`${apiUri}/count`, {
    params: {
      search,
      ...restParams,
    },
  })

  const initData = () => {
    fetchData()
    fetchCount()
  }

  // init data on mount and on restParams and search update
  useEffect(initData, [JSON.stringify(restParams), search])

  if (totalCount < count) {
    setTotalCount(count)
  }

  useOnUpdate(() => {
    fetchData()
  }, [limit, offset, sort.by, sort.order])

  const handleSortBy = useCallback(
    (orderByField) => {
      const currentOrder = sort.by === orderByField ? sort.order : null
      let order = SortOrder.asc
      if (currentOrder === SortOrder.asc) {
        order = SortOrder.desc
      } else if (currentOrder === SortOrder.desc) {
        order = null
      }

      const sortUpdated = { by: orderByField, order }
      updateQuery(navigate)({ sort: sortUpdated, offset: null })
      dispatch(TablesActions.updateSort({ module, sort: sortUpdated }))
    },
    [dispatch, module, navigate, sort.by, sort.order]
  )

  const handleSearch = useCallback(
    (searchText) => {
      updateQuery(navigate)({ search: searchText, offset: null })
    },
    [navigate]
  )

  // on rest params and limit update, go to first page (reset offset)
  useOnUpdate(() => {
    updateQuery(navigate)({ offset: null })
  }, [JSON.stringify(restParams), limit])

  // selected items (kept across pages)
  const [selectedItems, setSelectedItems] = useState([])

  const isItemSelected = useCallback(
    (item) => {
      const key = keyExtractor({ item })
      return selectedItems.some((selectedItem) => keyExtractor({ item: selectedItem }) === key)
    },
    [keyExtractor, selectedItems]
  )

  const allVisibleItemsSelected = list.length > 0 && list.every(isItemSelected)

  const selectAllItems = useCallback(() => {
    setSelectedItems([...selectedItems, ...list.filter((item) => !isItemSelected(item))])
  }, [isItemSelected, list, selectedItems])

  const deselectAllItems = useCallback(() => {
    setSelectedItems([])
  }, [])

  // reset selected items when filter, search or page size change, or data is refreshed (e.g. records update)
  useOnUpdate(() => {
    deselectAllItems()
  }, [JSON.stringify(restParams), search, limit])

  const onRowClick = useCallback(
    async (item) => {
      if (onRowClickProp) {
        await onRowClickProp(item)
      }
      if (selectable && selectOnClick) {
        const key = keyExtractor({ item })
        const selectedItemsUpdated = ArrayUtils.addOrRemoveItem({
          item,
          compareFn: (_item) => keyExtractor({ item: _item }) === key,
        })
        setSelectedItems(selectedItemsUpdated)
      }
    },
    [keyExtractor, onRowClickProp, selectOnClick, selectable]
  )

  const onVisibleColumnsChange = useCallback(
    (visibleColumnKeysUpdated) => {
      dispatch(TablesActions.updateVisibleColumns({ module, visibleColumns: visibleColumnKeysUpdated }))
    },
    [dispatch, module]
  )

  return {
    loadingData,
    loadingCount,
    list,
    offset,
    limit,
    sort,
    search,
    handleSearch,
    handleSortBy,
    count: Number(count),
    totalCount: Number(totalCount),
    initData,
    onRowClick,
    onVisibleColumnsChange,
    selectedItems,
    selectAllItems,
    deselectAllItems,
    visibleColumnKeys,
    visibleColumns,
    allVisibleItemsSelected,
  }
}
